import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Check, Copy as CopyIcon, Gift, Share2, X } from 'lucide-react';
import { PromoCard } from '@ck/ui';
import { usd } from '@ck/sdk';
import { api } from './api';

interface Popup { id: string; place: string; occasion: string; theme: string; title: string; body: string; ctaLabel: string; ctaUrl: string; couponCode: string | null; image: string | null; endsAt: string | null }
interface Promo {
  offer: null | { code: string; title: string; description: string; percent_off: number; expires_at: string | null; prices: { planId: string; name: string; sizes: { size: number; priceUsd: number; nowUsd: number }[] }[] };
  coupons: { code: string; percent_off: number; title: string | null; note: string | null; size: number | null; expires_at: string | null }[];
  requests: { id: string; plan_name: string; size: number; status: string }[];
  challenges: { planId: string; name: string; brand: string; sizes: number[] }[];
  popups?: Popup[];
  shareUrl: string;
}
type Slide = { key: string; kind: 'coupon'; c: Promo['coupons'][number] } | { key: string; kind: 'offer'; o: NonNullable<Promo['offer']> } | { key: string; kind: 'popup'; p: Popup };

const left = (iso: string | null, now: number) => {
  if (!iso) return null;
  const ms = Math.max(0, Date.parse(iso) - now);
  return { d: Math.floor(ms / 86_400_000), h: Math.floor(ms / 3_600_000) % 24, m: Math.floor(ms / 60_000) % 60, s: Math.floor(ms / 1000) % 60, over: ms === 0 };
};
const k = (n: number) => (n >= 1000 ? `${n / 1000}K` : String(n));

/**
 * Shown every time the client signs in or reloads: their personal coupon, the offer for everyone and any pop-up
 * the team built in the CRM, one after another. "No thanks" hides that coupon or offer for good. With nothing to show,
 * the client can ask for a coupon.
 */
export function OfferPopup({ buy }: { buy: () => void }) {
  const [p, setP] = useState<Promo | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [at, setAt] = useState(0);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    api.get<Promo>('/v1/me/promo').then((d) => {
      setP(d);
      setSlides([
        ...d.coupons.slice(0, 3).map((c) => ({ key: `c-${c.code}`, kind: 'coupon' as const, c })),
        ...(d.offer ? [{ key: `o-${d.offer.code}`, kind: 'offer' as const, o: d.offer }] : []),
        ...(d.popups ?? []).map((x) => ({ key: `p-${x.id}`, kind: 'popup' as const, p: x })),
      ]);
      setOpen(true);
    }).catch(() => undefined);
  }, []);
  useEffect(() => { if (!open) return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [open]);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open]);
  const cur = slides[Math.min(at, slides.length - 1)];
  useEffect(() => { if (open && cur?.kind === 'popup') void api.post(`/v1/popups/${cur.p.id}/seen`, {}).catch(() => undefined); }, [open, cur?.key]);
  if (!open || !p) return null;
  const close = () => setOpen(false);
  const next = () => (at < slides.length - 1 ? setAt(at + 1) : close());
  const use = (code: string | null) => { try { if (code) sessionStorage.setItem('ck.coupon', code); } catch { /* ignore */ } close(); buy(); };
  const decline = async (code: string) => {
    await api.post('/v1/me/promo/decline', { code }).catch(() => undefined);
    const rest = slides.filter((x) => x !== cur);
    if (!rest.length) return close();
    setSlides(rest); setAt(Math.min(at, rest.length - 1));
  };
  const steps = slides.length > 1 ? <div className="pp-steps" role="tablist" aria-label="Offers">{slides.map((x, i) => <button key={x.key} role="tab" aria-selected={i === at} aria-label={`${i + 1} of ${slides.length}`} className={i === at ? 'on' : ''} onClick={() => setAt(i)} />)}</div> : null;
  return (
    <div className="op-back" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      {!cur ? (
        <section className="op" role="dialog" aria-modal="true" aria-labelledby="op-h"><button className="op-x" aria-label="Close" onClick={close}><X /></button><Ask promo={p} /></section>
      ) : cur.kind === 'coupon' ? (
        <section key={cur.key} className="op" role="dialog" aria-modal="true" aria-labelledby="op-h"><button className="op-x" aria-label="Close" onClick={close}><X /></button>
          <Deal kicker="Your personal coupon" title={cur.c.title ?? 'A gift from our team'} pct={cur.c.percent_off} code={cur.c.code} note={cur.c.note ?? (cur.c.size ? `For a ${k(cur.c.size)} challenge.` : '')} ends={cur.c.expires_at} now={now} share={null} cta="Use my coupon" use={() => use(cur.c.code)} decline={() => void decline(cur.c.code)} steps={steps} />
        </section>
      ) : cur.kind === 'offer' ? (
        <section key={cur.key} className="op" role="dialog" aria-modal="true" aria-labelledby="op-h"><button className="op-x" aria-label="Close" onClick={close}><X /></button>
          <Deal kicker="Limited-time offer" title={cur.o.title} pct={cur.o.percent_off} code={cur.o.code} note={cur.o.description} ends={cur.o.expires_at} now={now} share={p.shareUrl} cta="View offer" use={() => use(cur.o.code)} decline={() => void decline(cur.o.code)} prices={cur.o.prices} steps={steps} />
        </section>
      ) : (
        <PromoCard key={cur.key} titleId="op-h" theme={cur.p.theme} occasion={cur.p.occasion} title={cur.p.title} body={cur.p.body} image={cur.p.image} couponCode={cur.p.couponCode}
          ctaLabel={cur.p.ctaLabel || (slides.length > 1 && at < slides.length - 1 ? 'Next' : '')} onClose={close} footer={steps}
          onCta={() => {
            const u = cur.p.ctaUrl;
            if (!cur.p.ctaLabel) return next();
            void api.post(`/v1/popups/${cur.p.id}/seen`, { click: true }).catch(() => undefined);
            if (!u || u === '#buy') return use(cur.p.couponCode);
            if (u.startsWith('#') || u.startsWith('/')) { close(); location.hash = u.replace(/^\//, '#/'); return; }
            window.open(u, '_blank', 'noopener'); close();
          }} />
      )}
    </div>
  );
}

/** The coupon design: big percentage, live countdown, prices with the discount, the code to copy. */
function Deal({ kicker, title, pct, code, note, ends, now, share, cta, use, decline, prices, steps }: { kicker: string; title: string; pct: number; code: string; note: string; ends: string | null; now: number; share: string | null; cta: string; use: () => void; decline: () => void; prices?: NonNullable<Promo['offer']>['prices']; steps: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const t = left(ends, now);
  const copy = async () => { try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard blocked */ } };
  async function doShare() {
    const text = `${title}: ${pct}% off with code ${code}`;
    try { if (navigator.share) await navigator.share({ title, text, url: share ?? undefined }); else { await navigator.clipboard.writeText(`${text} ${share ?? ''}`.trim()); setCopied(true); } } catch { /* cancelled */ }
  }
  const first = prices?.[0];
  return (
    <>
      <p className="op-kick"><Gift aria-hidden />{kicker}</p>
      <h2 id="op-h" className="op-h"><span className="op-pct">{pct}%</span> off<small>{title}</small></h2>
      {note && <p className="op-note">{note}</p>}
      {t && !t.over && (
        <div className="op-clock" role="timer" aria-label={`Ends in ${t.d} days ${t.h} hours ${t.m} minutes`}>
          {[[t.d, 'days'], [t.h, 'hours'], [t.m, 'min'], [t.s, 'sec']].map(([v, l]) => <span key={l as string}><b>{String(v).padStart(2, '0')}</b><small>{l}</small></span>)}
        </div>
      )}
      {first && first.sizes.length > 0 && (
        <div className="op-prices" aria-label={`${first.name} prices with the offer`}>
          {first.sizes.map((s) => <span key={s.size}><small>{k(s.size)}</small><s>{usd(s.priceUsd, 0)}</s><b>{usd(s.nowUsd)}</b></span>)}
        </div>
      )}
      <div className="op-code">
        <span><small>Your code</small><b className="mono">{code}</b></span>
        <button className="btn ghost sm" onClick={() => void copy()}>{copied ? <><Check />Copied</> : <><CopyIcon />Copy</>}</button>
      </div>
      <div className="op-actions">
        {share && <button className="btn ghost" onClick={() => void doShare()}><Share2 />Share</button>}
        <button className="btn" onClick={use}>{cta}</button>
      </div>
      <button type="button" className="op-no" onClick={decline}>No thanks, I don’t want this offer</button>
      {steps}
    </>
  );
}

function Ask({ promo }: { promo: Promo }) {
  const pending = promo.requests.find((r) => r.status === 'pending');
  const [planId, setPlanId] = useState(promo.challenges[0]?.planId ?? '');
  const plan = promo.challenges.find((c) => c.planId === planId);
  const [size, setSize] = useState<number>(plan?.sizes[0] ?? 0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => { if (plan && !plan.sizes.includes(size)) setSize(plan.sizes[0] ?? 0); }, [planId]);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr('');
    try { await api.post('/v1/me/coupon-requests', { planId, size, note }); setSent(true); }
    catch (x) { setErr(x instanceof Error ? x.message : 'Could not send the request.'); }
    finally { setBusy(false); }
  }
  if (pending || sent) return (
    <>
      <p className="op-kick"><Gift aria-hidden />Coupon request</p>
      <h2 id="op-h" className="op-h">Request received<small>{pending ? `${pending.plan_name} · ${k(pending.size)}` : `${plan?.name ?? ''} · ${k(size)}`}</small></h2>
      <p className="op-note">Our team will send a coupon to your portal under My Offers and to your email. You will see it here next time you sign in.</p>
    </>
  );
  if (!promo.challenges.length) return null;
  return (
    <form onSubmit={submit}>
      <p className="op-kick"><Gift aria-hidden />No offer running right now</p>
      <h2 id="op-h" className="op-h">Apply for a<br />coupon code<small>Tell us which challenge you want. We send coupons to your portal and email.</small></h2>
      <div className="op-form">
        <label>Challenge<select value={planId} onChange={(e) => setPlanId(e.target.value)}>{promo.challenges.map((c) => <option key={c.planId} value={c.planId}>{c.name}</option>)}</select></label>
        <label>Account size<select value={size} onChange={(e) => setSize(Number(e.target.value))}>{(plan?.sizes ?? []).map((s) => <option key={s} value={s}>{k(s)}</option>)}</select></label>
        <label className="op-wide">Anything we should know? <small>Optional</small><textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      {err && <p className="msg err" role="alert">{err}</p>}
      <div className="op-actions"><button className="btn" disabled={busy || !planId || !size}>{busy ? 'Sending…' : 'Apply for coupon'}</button></div>
    </form>
  );
}
